import { useCallback, useState } from "react";
import { useApi } from "../apiCall/useApi";
import Joi from "joi";
import { Urls } from "../../../utils/constant";
import { size } from "lodash";
import { useSnackbar } from "notistack";

import {
  hidePageLoader,
  showPageLoader,
} from "../../reducers/Common/PageLoader/PageLoader.reducer";
import { useDispatch, useSelector } from "react-redux";

import useFullPageLoader from "../Common/PageLoader/useFullPageLoader";

import {
  getDoctorList,
  getLeaveList,
  getDoctorHolidayList,
} from "../../selectors/DoctorHoliday/doctorHoliday.selector";
import { setDoctorHolidayData } from "../../reducers/DoctorHoliday/doctorHolidayForm.reducer";
import { format, parse } from "date-fns";

export const DoctorHolidayEntrySchema = Joi.object({
  name: Joi.string().required().messages({
    "string.empty": "Please Select a Doctor Name ",
  }),
  leave: Joi.string().required().messages({
    "string.empty": "Please Select a Leave Type ",
  }),
});

export function useDoctorHolidayForm({
  existingStatus,
  existingDoctorName,
  existingLeaveName,
  existingLeaveDate,
}) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { apiPost } = useApi();
  const [loading, setLoading] = useState(false);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const doctorList = useSelector(getDoctorList);
  const leaveList = useSelector(getLeaveList);
  const doctorHolidayList = useSelector(getDoctorHolidayList);
  const [status, setStatus] = useState(existingStatus === 2 ? 0 : 1);
  const [DoctorId, setDoctorId] = useState(existingDoctorName);
  const [LeaveId, setLeaveId] = useState(existingLeaveName);
  const [LeaveDate, setLeaveDate] = useState(existingLeaveDate);
  const [Id, setId] = useState("");
  const [appointmentWarning, setAppointmentWarning] = useState(null);

  const handleDeleteId = useCallback(
    (row) => {
      setId(row.id);
      setDoctorId(row.doctor_id);
      setLeaveId(row.leave_type_id);
      setLeaveDate(row.from_date_formate);
    },
    [Id, DoctorId, LeaveId, LeaveDate]
  );
  const DoctorHolidayEntry = useCallback(
    async ({ }) => {
      if (loading) return;
      setLoading(true);
      try {
        const formattedLeaveDate =
          typeof LeaveDate === "string"
            ? format(new Date(LeaveDate), "yyyy-MM-dd")
            : format(LeaveDate, "yyyy-MM-dd");

        const isDuplicate = doctorHolidayList.some(
          (item) =>
            item.doctor_id === DoctorId &&
            format(new Date(item.from_date), "yyyy-MM-dd") === formattedLeaveDate &&
            item.status !== 2
        );

        if (isDuplicate) {
          enqueueSnackbar("This doctor already has a holiday on this date.", {
            variant: "error",
          });
          setLoading(false);
          return false;
        }

        dispatch(showPageLoader());

        const status_Val = status === false ? 2 : status === 0 ? 2 : 1;
        const { data } = await apiPost({
          url: Urls.doctorHolidayAdd(),

          data: {
            doctor_id: DoctorId,
            from_date: formattedLeaveDate,
            leave_type_id: LeaveId,
            status: status_Val,
            insert_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });

        if (size(data.id) === 36) {
          enqueueSnackbar("Holiday Data Added Successfully.", {
            variant: "success",
          });
          return true;
        }

        // Backend returned a message (e.g. appointment conflict)
        if (data.message) {
          setAppointmentWarning({ message: data.message });
        }

        setLoading(false);
        dispatch(hidePageLoader());
        return false;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Problem While Saving Holiday Data", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, DoctorId, status, LeaveDate, LeaveId, doctorHolidayList, enqueueSnackbar, dispatch, apiPost, LeaveId]
  );

  const DoctorHolidayUpdate = useCallback(
    async ({ id }) => {
      if (loading) return;
      setLoading(true);
      try {
        const formattedLeaveDate = format(new Date(LeaveDate), "yyyy-MM-dd");

        const isDuplicate = doctorHolidayList.some(
          (item) =>
            item.id !== id &&
            item.doctor_id === DoctorId &&
            format(new Date(item.from_date), "yyyy-MM-dd") === formattedLeaveDate &&
            item.status !== 2
        );

        if (isDuplicate) {
          enqueueSnackbar("This doctor already has a holiday on this date.", {
            variant: "error",
          });
          setLoading(false);
          return false;
        }

        dispatch(showPageLoader());

        const status_Val = status === false ? 2 : status === 0 ? 2 : 1;
        const { data } = await apiPost({
          url: Urls.doctorHolidayUpdate(),
          data: {
            id: id,
            doctor_id: DoctorId,
            from_date: formattedLeaveDate,
            leave_type_id: LeaveId,
            status: status_Val,
            insert_by: "",
          },
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        if (size(data.id) === 36) {
          enqueueSnackbar("Holiday Data Updated Successfully.", {
            variant: "success",
          });
          return true;
        }

        // Backend returned a message (e.g. appointment conflict)
        if (data.message) {
          setAppointmentWarning({ message: data.message });
        }

        setLoading(false);
        dispatch(hidePageLoader());
        return false;
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        enqueueSnackbar("Problem While Saving Holiday Data", {
          variant: "error",
        });
        return false;
      }
    },
    [loading, DoctorId, status, LeaveDate, LeaveId, doctorHolidayList, enqueueSnackbar, dispatch, apiPost]
  );

  const DoctorHolidayDelete = useCallback(async () => {
    const parsedDate = parse(LeaveDate, "dd-MM-yyyy", new Date());
    const formattedLeaveDate = format(parsedDate, "yyyy-MM-dd");

    try {
      dispatch(showPageLoader());
      const { data } = await apiPost({
        url: Urls.doctorHolidayUpdate(),
        data: {
          id: Id,
          doctor_id: DoctorId,
          leave_type_id: LeaveId,
          from_date: formattedLeaveDate,
          status: 2,
          insert_by: "",
        },
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (size(data?.id) === 36) {
        enqueueSnackbar("Holiday Data Deleted Successfully.", {
          variant: "success",
        });
        return true;
      }

      setLoading(false);
      dispatch(hidePageLoader());
      return false;
    } catch (err) {
      dispatch(hidePageLoader());
      console.log(err);
      setLoading(false);
      enqueueSnackbar("Problem While Deleting Holiday Data", {
        variant: "error",
      });
      return false;
    }
  }, [status, Id, DoctorId, LeaveId, LeaveDate, apiPost, dispatch, enqueueSnackbar]);

  const findDoctorFromLabel = useCallback(
    (value) => {
      const selectedDoctorId = value
        ? doctorList.find((item) => item.label === value)?.id
        : null;
      setDoctorId(selectedDoctorId);
      return selectedDoctorId;
    },
    [doctorList]
  );
  const findLeaveLabel = useCallback(
    (value) => {
      const selectedLeaveId = value
        ? leaveList.find((item) => item.label === value)?.id
        : null;
      setLeaveId(selectedLeaveId);
      return selectedLeaveId;
    },
    [leaveList]
  );

  const getDoctorHolidayById = useCallback(
    async (id) => {
      if (loading) return;
      setLoading(true);
      try {
        dispatch(showPageLoader());

        const { data } = await apiPost({
          url: Urls.getHolidaybyId(),
          data: {
            id: id,
          },
        });

        dispatch(setDoctorHolidayData(data));
        setLoading(false);
        dispatch(hidePageLoader());
      } catch (err) {
        dispatch(hidePageLoader());
        console.log(err);
        setLoading(false);
        return false;
      }
    },
    [loading, apiPost, dispatch]
  );
  const handleLeaveDateChange = useCallback((newDate) => {
    if (newDate === null) return;
    setLeaveDate(newDate);
  }, []);

  const handleStatusComboChange = useCallback((event) => {
    if (event.target.checked === 1) {
      setStatus(event.target.checked);
    } else {
      setStatus(event.target.checked);
    }
  }, []);

  return {
    loader,
    loading,
    DoctorHolidayEntrySchema,
    handleStatusComboChange,
    DoctorId,
    status,
    DoctorHolidayUpdate,
    DoctorHolidayEntry,
    findDoctorFromLabel,
    findLeaveLabel,
    getDoctorHolidayById,
    LeaveId,
    handleLeaveDateChange,
    handleDeleteId,
    DoctorHolidayDelete,
    appointmentWarning,
    setAppointmentWarning,
  };
}
